import { Channel, DUCKING, SoundActor, VoicePriority, VoiceTable, busGain, duckedGain, placedLevel, type ChannelSpec } from "odyssey-shared";
import { MASTER, type AudioSettings } from "./audio-settings.ts";
import type { AudioVoice } from "./report.ts";
import type { ContextLike, GainLike, PannerLike, ParamLike, SourceLike } from "./graph.ts";

/**
 * One channel of the mixer (ADR-085 decisions 8 to 10): its nodes, its voice table and, beside the table, what the
 * mixer keeps for each voice. Made once with the context by `channelNodes` and written in place by the mixer.
 */

/**
 * Seconds a gain takes to settle at a new level, as `setTargetAtTime`'s time constant.
 */
export const LEVEL_TIME = 0.03;

/**
 * How a voice's sound is heard: `UNPLACED` at its own level wherever the listener is (a free voice too), `ACROSS_VISION`
 * from its place across the vision rectangle (`positional`), `BY_DISTANCE` from its place by the straight distance
 * (`playerPositional`).
 */
export const UNPLACED = 0;
export const ACROSS_VISION = 1;
export const BY_DISTANCE = 2;

/**
 * One channel's nodes and voices. Beside the voice table, by voice: the hook it plays, how it is heard (`UNPLACED`,
 * `ACROSS_VISION` or `BY_DISTANCE`), its source's offset in tiles from its anchor, whether it has its anchor yet and
 * the anchor (where the listener was in the first frame after it started), its hook's trim, and the level and pan
 * last asked of its gain and panner. All are made once with the context and written in place.
 */
export interface ChannelNodes {
  readonly id: Channel;
  readonly volume: GainLike;
  readonly duck: GainLike;
  readonly table: VoiceTable;
  readonly gains: GainLike[];
  readonly panners: PannerLike[];
  readonly sources: (SourceLike | null)[];
  readonly hooks: string[];
  readonly heard: Uint8Array;
  readonly offsetX: Float64Array;
  readonly offsetY: Float64Array;
  readonly anchored: Uint8Array;
  readonly anchorX: Float64Array;
  readonly anchorY: Float64Array;
  readonly trims: Float64Array;
  readonly levels: Float64Array;
  readonly pans: Float64Array;
}

/**
 * Sets a parameter to a value now, ending whatever it was easing towards: a voice taken for a new sound starts at the
 * new sound's value, never part way from the last one's.
 *
 * @param param The parameter.
 * @param value The value.
 * @param now The context's time.
 */
export function setNow(param: ParamLike, value: number, now: number): void {
  param.cancelScheduledValues(now);
  param.setValueAtTime(value, now);
}

/**
 * Stops a voice's source at once, whatever state it is in: a source already scheduled to stop (a fading voice) is
 * stopped a second time here, which the Web Audio specification allows and an older engine may refuse by throwing;
 * a refusal is swallowed, since the source then ends at its scheduled stop and its voice is being retaken either way.
 *
 * @param source The source, or null for a free voice.
 */
export function stopQuietly(source: SourceLike | null): void {
  try {
    source?.stop();
  } catch {
    return;
  }
}

/**
 * The priority a sound keeps its voice by.
 *
 * @param channel Its channel.
 * @param actor Who it belongs to.
 * @returns The priority.
 */
export function priorityOf(channel: Channel, actor: SoundActor): VoicePriority {
  if (channel === Channel.Ui || channel === Channel.Stingers) {
    return VoicePriority.Interface;
  }
  return actor === SoundActor.Own ? VoicePriority.Own : VoicePriority.Positional;
}

/**
 * Makes one channel's nodes under Master: its volume and ducking gains, and for each voice a gain into a panner into
 * the volume, with the voice table and the per-voice records, every voice free.
 *
 * @param context The audio context.
 * @param spec The channel and how many voices it holds.
 * @param master Master's gain.
 * @param perHook How many sounds of one hook the channel plays at once.
 * @returns The channel's nodes.
 */
export function channelNodes(context: ContextLike, spec: ChannelSpec, master: GainLike, perHook: number): ChannelNodes {
  const volume = context.createGain();
  const duck = context.createGain();
  volume.connect(duck);
  duck.connect(master);
  const gains: GainLike[] = [];
  const panners: PannerLike[] = [];
  for (let index = 0; index < spec.voices; index += 1) {
    const gain = context.createGain();
    const panner = context.createStereoPanner();
    gain.connect(panner);
    panner.connect(volume);
    gains.push(gain);
    panners.push(panner);
  }
  const nodes: ChannelNodes = {
    id: spec.id,
    volume,
    duck,
    table: new VoiceTable(spec.voices, perHook),
    gains,
    panners,
    sources: gains.map(() => null),
    hooks: gains.map(() => ""),
    heard: new Uint8Array(spec.voices),
    offsetX: new Float64Array(spec.voices),
    offsetY: new Float64Array(spec.voices),
    anchored: new Uint8Array(spec.voices),
    anchorX: new Float64Array(spec.voices),
    anchorY: new Float64Array(spec.voices),
    trims: new Float64Array(spec.voices),
    levels: new Float64Array(spec.voices),
    pans: new Float64Array(spec.voices),
  };
  return nodes;
}

/**
 * Eases every placed voice that plays to the level and pan the listener's place gives it (`placedLevel`), and writes
 * that level into the channel's voice table, so the order voices give way in reads what is heard now. A voice whose
 * level and pan come out as last asked is left alone; a voice started since the last frame takes this listener as
 * its anchor and is asked nothing. Nothing here allocates.
 *
 * @param voiced Every channel's nodes.
 * @param x Where the listener is across, in tiles.
 * @param y The same down.
 * @param now The context's time.
 * @param level A scratch record the level and pan are worked out into.
 */
export function followListener(voiced: readonly ChannelNodes[], x: number, y: number, now: number, level: { gain: number; pan: number }): void {
  for (let index = 0; index < voiced.length; index += 1) {
    const nodes = voiced[index] as ChannelNodes;
    const heard = nodes.heard;
    for (let voice = 0; voice < heard.length; voice += 1) {
      const how = heard[voice] as number;
      if (how === UNPLACED) {
        continue;
      }
      if (nodes.anchored[voice] === 0) {
        nodes.anchored[voice] = 1;
        nodes.anchorX[voice] = x;
        nodes.anchorY[voice] = y;
        continue;
      }
      level.pan = nodes.pans[voice] as number;
      placedLevel(how === BY_DISTANCE, nodes.offsetX[voice] as number, nodes.offsetY[voice] as number, x - (nodes.anchorX[voice] as number), y - (nodes.anchorY[voice] as number), nodes.trims[voice] as number, level);
      if (level.gain !== nodes.levels[voice]) {
        nodes.levels[voice] = level.gain;
        nodes.table.setLevel(voice, level.gain);
        (nodes.gains[voice] as GainLike).gain.setTargetAtTime(level.gain, now, LEVEL_TIME);
      }
      if (level.pan !== nodes.pans[voice]) {
        nodes.pans[voice] = level.pan;
        (nodes.panners[voice] as PannerLike).pan.setTargetAtTime(level.pan, now, LEVEL_TIME);
      }
    }
  }
}

/**
 * The voices playing now, for the report: each with its hook, its channel, whether it is placed and the level and
 * pan last asked of it.
 *
 * @param voiced Every channel's nodes.
 * @returns The voices in channel then voice order, built when read.
 */
export function playingVoices(voiced: readonly ChannelNodes[]): AudioVoice[] {
  const out: AudioVoice[] = [];
  for (const nodes of voiced) {
    for (let voice = 0; voice < nodes.sources.length; voice += 1) {
      if (nodes.sources[voice] !== null) {
        out.push({ hook: nodes.hooks[voice] as string, channel: nodes.id, placed: nodes.heard[voice] !== UNPLACED, level: nodes.levels[voice] as number, pan: nodes.pans[voice] as number });
      }
    }
  }
  return out;
}

/**
 * Sets Master's and every channel's gain from the settings.
 *
 * @param context The context, or null before it exists.
 * @param master Master's gain, or null before it exists.
 * @param channels The channels.
 * @param settings The levels.
 */
export function applyLevels(context: ContextLike | null, master: GainLike | null, channels: ReadonlyMap<Channel, ChannelNodes>, settings: AudioSettings): void {
  if (context === null || master === null) {
    return;
  }
  const level = settings.levels[MASTER];
  master.gain.setTargetAtTime(busGain(level.volume, level.muted), context.currentTime, LEVEL_TIME);
  for (const [id, nodes] of channels) {
    const own = settings.levels[id];
    nodes.volume.gain.setTargetAtTime(busGain(own.volume, own.muted), context.currentTime, LEVEL_TIME);
  }
}

/**
 * Sets each ducked channel's gain from what plays now, lowering over the rule's attack and restoring over its release.
 *
 * @param context The context, or null before it exists.
 * @param channels The channels.
 * @param playing How many voices play on a channel.
 */
export function applyDucking(context: ContextLike | null, channels: ReadonlyMap<Channel, ChannelNodes>, playing: (channel: Channel) => number): void {
  if (context === null) {
    return;
  }
  for (const rule of DUCKING) {
    const nodes = channels.get(rule.target);
    if (nodes === undefined) {
      continue;
    }
    const gain = duckedGain(rule.target, playing);
    nodes.duck.gain.cancelScheduledValues(context.currentTime);
    nodes.duck.gain.setTargetAtTime(gain, context.currentTime, (gain < 1 ? rule.attack : rule.release) / 3);
  }
}

/**
 * Silences everything but one channel before a sound is played through the background mute: every other channel's
 * volume is set to 0 at once, and, for the first such sound, whatever still plays on the channel itself is stopped,
 * since the suspension froze it mid-sound.
 *
 * @param voiced The channels.
 * @param channel The channel that stays heard.
 * @param now The context's time.
 * @param first Whether no other sound is being played through the mute.
 */
export function silenceAllBut(voiced: readonly ChannelNodes[], channel: Channel, now: number, first: boolean): void {
  for (const nodes of voiced) {
    if (nodes.id !== channel) {
      setNow(nodes.volume.gain, 0, now);
    } else if (first) {
      stopAll([nodes]);
    }
  }
}

/**
 * Stops every source that plays on the channels.
 *
 * @param voiced The channels.
 */
export function stopAll(voiced: readonly ChannelNodes[]): void {
  for (const nodes of voiced) {
    for (const source of nodes.sources) {
      stopQuietly(source ?? null);
    }
  }
}
